import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import Breadcrumb from '../components/Breadcrumb';
import PlantCard from '../components/PlantCard';
import { Loader2, Ruler, Sun } from 'lucide-react';
import type { Plant } from '../types/plant';

interface Collection {
  id: string;
  name: string;
  description: string;
  long_description: string;
  image_url: string;
  space_needed: string;
  sunlight_requirements: string;
  plants: Plant[];
}

export default function CollectionDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [collection, setCollection] = React.useState<Collection | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const { setItems } = useBreadcrumb();

  // Scroll to top on mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    if (collection) {
      setItems([
        { label: 'Home', path: '/' },
        { label: 'Collections', path: '/collections' },
        { label: collection.name, path: `/collections/${slug}` }
      ]);
    }
  }, [collection, slug, setItems]);

  React.useEffect(() => {
    async function fetchCollection() {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('collections')
          .select(`
            *,
            plants:collections_plants(
              plant:plants(*)
            )
          `)
          .eq('slug', slug)
          .single();

        if (error) throw error;
        
        if (data) {
          // Transform the data structure
          const transformedData = {
            ...data,
            plants: data.plants.map((item: any) => item.plant)
          };
          setCollection(transformedData);
        }
      } catch (err) {
        console.error('Error fetching collection:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }

    if (slug) {
      fetchCollection();
    }
  }, [slug]);

  const handlePlantClick = (plantId: string) => {
    navigate(`/plants/${plantId}`, {
      state: {
        from: {
          type: 'collection',
          collectionName: collection?.name,
          collectionSlug: slug
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error || !collection) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-red-600">
          {error || 'Collection not found'}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="relative h-[40vh] min-h-[300px] bg-gray-900">
        <img 
          src={collection.image_url} 
          alt={collection.name}
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/70" />
        <div className="absolute inset-0 flex items-center">
          <div className="container mx-auto px-4">
            <h1 className="text-4xl font-bold text-white mb-4">
              {collection.name}
            </h1>
            {collection.description && (
              <p className="text-xl text-gray-200 max-w-2xl">
                {collection.description}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <Breadcrumb />
        
        {collection.long_description && (
          <div className="mt-8 bg-white rounded-lg shadow-sm p-6">
            <p className="text-gray-700 leading-relaxed">
              {collection.long_description}
            </p>
          </div>
        )}

        <div className="mt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {collection.space_needed && (
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Ruler className="h-5 w-5 text-primary-500" />
                  <h3 className="font-semibold">Space Needed</h3>
                </div>
                <p className="text-gray-700">{collection.space_needed}</p>
              </div>
            )}
            
            {collection.sunlight_requirements && (
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Sun className="h-5 w-5 text-primary-500" />
                  <h3 className="font-semibold">Sunlight Requirements</h3>
                </div>
                <p className="text-gray-700">{collection.sunlight_requirements}</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">What to Plant</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {collection.plants.map((plant) => (
              <PlantCard 
                key={plant.id}
                plant={plant}
                onClick={() => handlePlantClick(plant.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}